import React, { useEffect, useMemo, useRef, useState } from "react";

import { routes } from "Constant/routes";
import "css/createSourceModal.css";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { addSourceToTracker } from "helpers/Sources/createSource";

import { GenericModal } from "components/GenericModal";
import {
    creationConfigureContentSx,
    creationContentSx,
    creationFooterSx,
    creationHeaderSx,
    creationPaperSx,
    useTypeCatalog,
} from "components/organisms/CreationModal";
import { WIZARD_MODAL_CONTEXT } from "components/organisms/SignalWizard/SignalWizard.constants";
import {
    buildSourceCatalogItems,
    useSourceCreation,
} from "components/organisms/SourceCreation";
import { useTrackerStep } from "components/organisms/TrackerStep";
import SwalAlertComp from "components/SwalAlert";

import { closeModal } from "../../../redux/slices/modal/modal.slice";
import {
    getAllSourcesRedux,
    getJsSourcesRedux,
} from "../../../redux/slices/sources/sources.asyncActions";
import { getSingleTrackerRedux } from "../../../redux/slices/trackers/trackers.asyncActions";
import { setPreSelectedSourceId } from "../../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";
import {
    getCreateSourceHeaderProps,
    renderCreateSourceContent,
    renderCreateSourceFooter,
} from "./CreateSourceModal.helpers";
import { Step } from "./CreateSourceModal.types";

const WEB_SOURCE_CODES = ["js", "redirect"];

/**
 * Modal host for the shared source creation flow. Owns only modal concerns:
 * the type catalog step, closing, the "attach to tracker" step and the
 * redirect to the wizard.
 */
const CreateSourceModal = ({
    data,
    trackers,
    sourcesTypes,
    sourcesTypesStatus,
    language,
    singleTracker,
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const { tracker_id: trackerId, sourceType: lockedSourceType } = data;
    const isWizardContext = data?.context === WIZARD_MODAL_CONTEXT;

    const [step, setStep] = useState(
        lockedSourceType ? Step.INFO : Step.CATALOG,
    );
    const [pendingType, setPendingType] = useState(lockedSourceType ?? null);
    const [createdSourceId, setCreatedSourceId] = useState(null);

    const getTracker =
        trackerId && (() => dispatch(getSingleTrackerRedux(trackerId)));

    const handleGetSources =
        (location.pathname === routes.sources || isWizardContext) &&
        (() => dispatch(getAllSourcesRedux()));

    useEffect(() => {
        dispatch(getJsSourcesRedux());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Pre-fill the upload name with the tracker's first web source name. */
    const initialUploadName = useMemo(() => {
        const trackerSources = singleTracker?.tracker?.sources ?? [];
        return (
            trackerSources.find((source) =>
                WEB_SOURCE_CODES.includes(source?.source_type?.code),
            )?.name ?? ""
        );
    }, [singleTracker]);

    const handleClose = () => {
        dispatch(closeModal());
        handleGetSources && !trackerId && handleGetSources();
    };

    const addToTracker = async (id) => {
        if (!trackerId) return false;
        const ids = Array.isArray(id) ? id : [id];
        let allSucceeded = true;
        for (const sourceId of ids) {
            const response = await addSourceToTracker(
                { sources: [sourceId] },
                trackerId,
            );
            if (!response.status) {
                allSucceeded = false;
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        }
        handleClose();
        if (allSucceeded) getTracker();
        return allSucceeded;
    };

    const handleSourceCreated = async ({
        sourceId,
        isTrackerHandledByChild,
    }) => {
        setCreatedSourceId(sourceId);
        if (isWizardContext) {
            dispatch(setPreSelectedSourceId(sourceId));
            handleClose();
        } else if (trackerId) {
            if (!isTrackerHandledByChild) await addToTracker(sourceId);
        } else if (trackers?.length) {
            setStep(Step.TRACKER);
        } else {
            dispatch(setPreSelectedSourceId(sourceId));
            handleClose();
            navigate("/wizard");
        }
    };

    const sourceCreation = useSourceCreation({
        language,
        sourceTypes: sourcesTypes,
        onCreated: handleSourceCreated,
        onCancel: handleClose,
        hostActions: { addToTracker, getTracker, handleGetSources, trackerId },
        initialUploadName,
        initialSourceType: lockedSourceType,
        isTypeLocked: Boolean(lockedSourceType),
    });

    // Catalog step
    const catalogItems = useMemo(
        () => buildSourceCatalogItems(sourcesTypes, language),
        [sourcesTypes, language],
    );
    const catalog = useTypeCatalog(catalogItems);
    // The types request is dispatched on mount; until it has been observed
    // loading at least once an empty list means "not fetched yet". Protection
    const hasObservedLoadingRef = useRef(false);
    if (sourcesTypesStatus === "loading") hasObservedLoadingRef.current = true;
    const isCatalogLoading =
        sourcesTypesStatus === "loading" ||
        (!catalogItems.length &&
            !hasObservedLoadingRef.current &&
            sourcesTypesStatus !== "failed");

    const selectedItem = useMemo(
        () =>
            catalogItems.find(
                (item) =>
                    item.code ===
                    (sourceCreation.form.sourceType ?? pendingType), // TODO: may be not necessary
            ),
        [catalogItems, sourceCreation.form.sourceType, pendingType],
    );

    const continueToConfigure = (code = pendingType) => {
        if (!code) return;
        setPendingType(code);
        sourceCreation.selectSourceType(code);
        setStep(Step.INFO);
    };

    const backToCatalog = () => {
        sourceCreation.resetFormState();
        setStep(Step.CATALOG);
    };

    const canGoBack =
        !sourceCreation.isTypeLocked && sourceCreation.canChangeType;

    const isMappingView = sourceCreation.mapping.isMappingView;

    const trackerStep = useTrackerStep({
        sourceId: createdSourceId,
        language,
        handleClose,
        handleGetSources,
    });

    const headerProps = getCreateSourceHeaderProps({
        step,
        language,
        catalog,
        isMappingView,
        canGoBack,
        onBack: backToCatalog,
    });

    const content = renderCreateSourceContent({
        step,
        language,
        catalog,
        selectedItem,
        pendingType,
        onPendingTypeChange: setPendingType,
        onConfirmType: continueToConfigure,
        isCatalogLoading,
        sourceCreation,
        isMappingView,
        trackers,
        trackerStep,
    });

    const footer = renderCreateSourceFooter({
        step,
        language,
        selectedItem,
        pendingType,
        onConfirmType: continueToConfigure,
        handleClose,
        sourceCreation,
        isMappingView,
        canGoBack,
        onBack: backToCatalog,
        trackerStep,
    });

    return (
        <GenericModal
            open
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            paperSx={creationPaperSx}
            headerSx={creationHeaderSx}
            contentSx={
                step === Step.INFO && !isMappingView
                    ? creationConfigureContentSx
                    : creationContentSx
            }
            footerSx={creationFooterSx}
            scrollToTopKey={`${step}:${isMappingView}`}
            {...headerProps}
            content={content}
            footer={footer || undefined}
        />
    );
};

export default CreateSourceModal;
